import { useState, useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Paintbrush, Sparkles, AlertTriangle, X,
  Image as ImageIcon, Eye, Loader2, ChevronLeft, ChevronRight,
  FolderOpen,
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useThemePresets, useThemePresetFull } from '@/hooks/use-theme-presets'
import { useApplyTheme } from '@/hooks/use-apply-theme'
import { useAuthStore } from '@/stores/auth-store'
import type { ThemePresetSummary, ThemePresetFull } from '@/hooks/use-theme-presets'
import { FeatureGate } from '@/components/feature-gate'

const EDGE_API = import.meta.env.VITE_EDGE_API_URL ?? ''

// ── Types ─────────────────────────────────────────────────────────────────────

type EnrichedPreset = ThemePresetSummary & {
  palette_group: string | null
  mode: 'dark' | 'light' | 'any'
}

// ── Page options ───────────────────────────────────────────────────────────────

const PAGE_OPTIONS = [
  { key: 'homepage_sections', label: 'Home'    },
  { key: 'page_about',        label: 'About'   },
  { key: 'page_events',       label: 'Events'  },
  { key: 'page_members',      label: 'Members' },
  { key: 'page_music',        label: 'Music'   },
  { key: 'page_shop',         label: 'Shop'    },
] as const satisfies ReadonlyArray<{ key: keyof ThemePresetFull; label: string }>

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildPreviewUrl(
  tenantSlug: string,
  blocks: ThemePresetFull['homepage_sections'],
  preset: ThemePresetFull,
) {
  const publicBase = import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined
  const base = publicBase
    ? `${publicBase}/${tenantSlug}/preview`
    : `https://${tenantSlug}.oneidol.id/preview`
  const enabled = [...blocks]
    .filter((b) => b.enabled)
    .sort((a, b) => a.order - b.order)
  const theme = {
    color_primary:       preset.color_primary,
    color_secondary:     preset.color_secondary,
    color_accent:        preset.color_accent,
    color_background:    preset.color_background,
    color_surface:       preset.color_surface,
    color_text_primary:  preset.color_text_primary,
    color_text_muted:    preset.color_text_muted,
    color_border:        preset.color_border,
    font_family_heading: preset.font_family_heading,
    font_family_body:    preset.font_family_body,
    border_radius_base:  preset.border_radius_base,
    custom_css:          preset.custom_css ?? '',
  }
  return (
    `${base}` +
    `?blocks=${encodeURIComponent(JSON.stringify(enabled))}` +
    `&full=1` +
    `&theme=${encodeURIComponent(JSON.stringify(theme))}`
  )
}

// ── Thumbnail iframe scaling ──────────────────────────────────────────────────

// Iframe renders at 1200×675 (16:9). Scale is measured per-card via ResizeObserver
// so the iframe fills the aspect-video container exactly at any card width.
const THUMB_IFRAME_WIDTH  = 1200
const THUMB_IFRAME_HEIGHT = 675 // 1200 × 9/16

// ── Color Swatch (horizontal strip) ──────────────────────────────────────────

function ColorSwatch({ preset }: { preset: ThemePresetSummary }) {
  const colors = [
    preset.color_primary, preset.color_secondary, preset.color_accent,
    preset.color_background, preset.color_surface, preset.color_text_primary,
    preset.color_text_muted, preset.color_border,
  ]
  return (
    <div className="flex gap-0.5 h-3 rounded overflow-hidden">
      {colors.map((color, i) => (
        <div key={i} style={{ background: color, flex: 1 }} title={color} />
      ))}
    </div>
  )
}

// ── Live Preview Thumb ────────────────────────────────────────────────────────

function LivePreviewThumb({ url }: { url: string }) {
  const [loaded, setLoaded] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0)

  // Measure actual card width so the 1200×675 iframe scales to fill exactly
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width
      if (w > 0) setScale(w / THUMB_IFRAME_WIDTH)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <div ref={wrapRef} className="absolute inset-0">
      {scale > 0 && (
        <>
          {!loaded && <div className="absolute inset-0 bg-muted animate-pulse" />}
          <iframe
            src={url}
            title="theme preview"
            onLoad={() => setLoaded(true)}
            style={{
              width: `${THUMB_IFRAME_WIDTH}px`,
              height: `${THUMB_IFRAME_HEIGHT}px`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              border: 'none',
              pointerEvents: 'none',
              opacity: loaded ? 1 : 0,
              transition: 'opacity 0.2s ease',
            }}
          />
        </>
      )}
    </div>
  )
}

// ── Preset Preview Modal ──────────────────────────────────────────────────────

function PresetPreviewModal({
  preset,
  full,
  tenantSlug,
  onClose,
}: {
  preset: ThemePresetSummary
  full: ThemePresetFull
  tenantSlug: string
  onClose: () => void
}) {
  const availablePages = (PAGE_OPTIONS as readonly (typeof PAGE_OPTIONS)[number][]).filter((opt) => {
    const val = full[opt.key]
    return Array.isArray(val) && val.length > 0
  })

  const [currentPageKey, setCurrentPageKey] = useState<keyof ThemePresetFull>(
    availablePages[0]?.key ?? 'homepage_sections'
  )

  const blocks = full[currentPageKey] as ThemePresetFull['homepage_sections']
  const iframeSrc = buildPreviewUrl(tenantSlug, blocks, full)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* Header */}
      <div className="flex h-12 shrink-0 items-center gap-3 border-b border-border px-4">
        <p className="text-sm font-semibold shrink-0">
          Preview — <span className="text-primary">{preset.name}</span>
        </p>

        {/* Page selector */}
        {availablePages.length > 1 && (
          <select
            value={currentPageKey as string}
            onChange={(e) => setCurrentPageKey(e.target.value as keyof ThemePresetFull)}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {availablePages.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
        )}

        <span className="ml-auto hidden text-xs text-muted-foreground sm:inline">
          ESC untuk tutup
        </span>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 hover:bg-muted/50 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Iframe */}
      <div className="flex-1 overflow-hidden">
        <iframe
          key={iframeSrc}
          src={iframeSrc}
          className="h-full w-full border-0"
          title={`Preview ${preset.name}`}
        />
      </div>
    </div>,
    document.body
  )
}

// ── Preset Card ───────────────────────────────────────────────────────────────

function PresetCard({
  preset,
  tenantSlug,
  onApply,
}: {
  preset: ThemePresetSummary
  tenantSlug: string | undefined
  onApply: () => void
}) {
  const qc = useQueryClient()
  const [previewLoading, setPreviewLoading] = useState(false)
  const [modalFull, setModalFull] = useState<ThemePresetFull | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [thumbVisible, setThumbVisible] = useState(false)

  // Trigger full-data fetch when card enters viewport (for thumbnail iframe)
  useEffect(() => {
    const el = cardRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setThumbVisible(true); observer.disconnect() } },
      { rootMargin: '100px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Fetch full preset data when visible — shares cache with prefetchFull / handlePreview
  const { data: fullData } = useQuery({
    queryKey: ['theme-preset-full', preset.id],
    queryFn: async () => {
      const res = await fetch(`${EDGE_API}/theme-presets/${preset.id}`)
      if (!res.ok) throw new Error('Gagal memuat detail tema')
      return res.json() as Promise<ThemePresetFull>
    },
    staleTime: 60_000,
    enabled: thumbVisible,
  })

  // Build thumbnail iframe URL when homepage sections are available
  const homepageBlocks = (fullData?.homepage_sections ?? []).filter((b) => b.enabled)
  const thumbUrl = homepageBlocks.length > 0 && tenantSlug && fullData
    ? buildPreviewUrl(tenantSlug, homepageBlocks, fullData) + '&demo=1'
    : null

  function prefetchFull() {
    qc.prefetchQuery({
      queryKey: ['theme-preset-full', preset.id],
      queryFn: async () => {
        const res = await fetch(`${EDGE_API}/theme-presets/${preset.id}`)
        if (!res.ok) throw new Error('Gagal memuat detail tema')
        return res.json() as Promise<ThemePresetFull>
      },
      staleTime: 60_000,
    })
  }

  async function handlePreview() {
    if (!tenantSlug) return
    setPreviewLoading(true)
    try {
      let full = qc.getQueryData<ThemePresetFull>(['theme-preset-full', preset.id])
      if (!full) {
        full = await qc.fetchQuery<ThemePresetFull>({
          queryKey: ['theme-preset-full', preset.id],
          queryFn: async () => {
            const res = await fetch(`${EDGE_API}/theme-presets/${preset.id}`)
            if (!res.ok) throw new Error('Gagal memuat detail tema')
            return res.json()
          },
          staleTime: 60_000,
        })
      }
      setModalFull(full)
    } catch {
      // silently fail — full data unavailable
    } finally {
      setPreviewLoading(false)
    }
  }

  return (
    <div
      ref={cardRef}
      className="rounded-xl border border-border bg-card overflow-hidden flex flex-col hover:border-primary/50 transition-colors group"
      onMouseEnter={prefetchFull}
    >
      {/* Thumbnail — live iframe when homepage_sections exist, else placeholder */}
      <div className="relative w-full aspect-video bg-muted overflow-hidden">
        {thumbUrl ? (
          <LivePreviewThumb url={thumbUrl} />
        ) : preset.thumbnail_url ? (
          <img
            src={preset.thumbnail_url}
            alt={preset.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
          </div>
        )}
        <div className="absolute bottom-0 inset-x-0 px-3 pb-2">
          <ColorSwatch preset={preset} />
        </div>
      </div>

      {/* Card body */}
      <div className="p-4 flex-1 flex flex-col gap-3">
        <div className="flex-1">
          <h3 className="font-semibold text-sm leading-tight">{preset.name}</h3>
          {preset.description && (
            <p className="mt-1 text-xs text-muted-foreground line-clamp-2 leading-relaxed">
              {preset.description}
            </p>
          )}
          <p className="mt-2 text-[10px] text-muted-foreground">
            Font: <span className="font-medium text-foreground">{preset.font_family_heading}</span>
            {preset.font_family_heading !== preset.font_family_body && (
              <span> / {preset.font_family_body}</span>
            )}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={handlePreview}
            disabled={previewLoading || !tenantSlug}
            className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50"
          >
            {previewLoading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Eye className="h-3 w-3" />}
            Preview
          </button>

          <button
            type="button"
            onClick={onApply}
            className="flex-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Terapkan
          </button>
        </div>
      </div>

      {/* Fullscreen preview modal */}
      {modalFull && tenantSlug && (
        <PresetPreviewModal
          preset={preset}
          full={modalFull}
          tenantSlug={tenantSlug}
          onClose={() => setModalFull(null)}
        />
      )}
    </div>
  )
}

// ── Skeleton Card ─────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden animate-pulse">
      <div className="h-28 bg-muted" />
      <div className="p-4 space-y-3">
        <div className="h-4 bg-muted rounded w-3/4" />
        <div className="h-3 bg-muted rounded w-full" />
        <div className="h-3 bg-muted rounded w-1/2" />
        <div className="h-7 bg-muted rounded mt-2" />
      </div>
    </div>
  )
}

// ── Folder Card (root view) ───────────────────────────────────────────────────

function FolderCard({
  name,
  presets,
  onClick,
}: {
  name: string
  presets: EnrichedPreset[]
  onClick: () => void
}) {
  // Show up to 4 color strip previews (prefer dark mode first)
  const previews = [
    ...presets.filter((p) => p.mode === 'dark'),
    ...presets.filter((p) => p.mode !== 'dark'),
  ].slice(0, 4)

  const darkCount  = presets.filter((p) => p.mode === 'dark').length
  const lightCount = presets.filter((p) => p.mode === 'light').length

  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-xl border border-border bg-card overflow-hidden text-left hover:border-primary/60 hover:shadow-md transition-all group w-full"
    >
      {/* Color strip previews */}
      <div className="h-24 bg-muted/50 p-2 flex flex-col gap-1 overflow-hidden">
        {previews.map((preset, i) => (
          <div key={i} className="flex gap-0.5 flex-1 rounded overflow-hidden">
            <div style={{ background: preset.color_background, flex: 3 }} />
            <div style={{ background: preset.color_surface, flex: 1 }} />
            <div style={{ background: preset.color_primary, flex: 1 }} />
            <div style={{ background: preset.color_accent, flex: 1 }} />
            <div style={{ background: preset.color_border, flex: 1 }} />
          </div>
        ))}
        {previews.length === 0 && (
          <div className="flex-1 flex items-center justify-center">
            <FolderOpen className="h-8 w-8 text-muted-foreground/20" />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 flex items-center gap-2.5">
        <FolderOpen className="h-4 w-4 text-primary shrink-0 group-hover:text-primary/80" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{name}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {darkCount > 0 && `${darkCount} dark`}
            {darkCount > 0 && lightCount > 0 && ' · '}
            {lightCount > 0 && `${lightCount} light`}
            {darkCount === 0 && lightCount === 0 && `${presets.length} preset`}
          </p>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:text-foreground transition-colors" />
      </div>
    </button>
  )
}

// ── Folder Contents View ──────────────────────────────────────────────────────

function FolderContentsView({
  name,
  presets,
  tenantSlug,
  onBack,
  onApply,
}: {
  name: string
  presets: EnrichedPreset[]
  tenantSlug: string | undefined
  onBack: () => void
  onApply: (preset: ThemePresetSummary) => void
}) {
  const modes = new Set(presets.map((p) => p.mode).filter((m) => m !== 'any'))
  const hasDark  = modes.has('dark')
  const hasLight = modes.has('light')
  const showFilter = hasDark && hasLight

  const [activeMode, setActiveMode] = useState<'dark' | 'light'>(hasDark ? 'dark' : 'light')

  const visible = showFilter
    ? presets.filter((p) => p.mode === activeMode || p.mode === 'any')
    : presets

  return (
    <div className="space-y-5">
      {/* Back + folder header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Kembali
        </button>
        <span className="text-muted-foreground">/</span>
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm">{name}</span>
          <span className="text-xs text-muted-foreground">({presets.length} preset)</span>
        </div>
      </div>

      {/* Mode filter */}
      {showFilter && (
        <div className="flex gap-1 rounded-lg border border-border bg-muted/40 p-1 w-fit">
          {(['dark', 'light'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setActiveMode(m)}
              className={[
                'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                activeMode === m
                  ? 'bg-card shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {m === 'dark' ? 'Dark Mode' : 'Light Mode'}
            </button>
          ))}
        </div>
      )}

      {/* Preset grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {visible.map((preset) => (
          <PresetCard
            key={preset.id}
            preset={preset}
            tenantSlug={tenantSlug}
            onApply={() => onApply(preset)}
          />
        ))}
      </div>
    </div>
  )
}

// ── Confirm Dialog ────────────────────────────────────────────────────────────

function ConfirmDialog({
  presetId, presetName, onConfirm, onCancel, isPending, error,
}: {
  presetId: string
  presetName: string
  onConfirm: () => void
  onCancel: () => void
  isPending: boolean
  error?: string | null
}) {
  const { isLoading: detailLoading } = useThemePresetFull(presetId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />
      <div
        className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card shadow-xl p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 rounded-full bg-amber-100 p-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h2 className="font-semibold text-base">Terapkan Tema: {presetName}?</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Tindakan ini akan menimpa semua pengaturan yang ada:
            </p>
          </div>
        </div>

        <ul className="text-sm space-y-1 pl-4 text-muted-foreground">
          <li className="list-disc">Warna (8 nilai) &amp; Font heading + body</li>
          <li className="list-disc">Border radius &amp; Custom CSS</li>
          <li className="list-disc">Layout Homepage</li>
          <li className="list-disc">Layout 5 halaman sistem (About, Events, Members, Music, Shop)</li>
        </ul>

        <p className="text-xs text-muted-foreground">
          Anda tetap bisa mengubah pengaturan secara manual setelahnya melalui menu Tema Visual dan Kelola Halaman.
        </p>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}

        <div className="flex gap-3 pt-1">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="flex-1 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50"
          >
            Batal
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending || detailLoading}
            className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isPending ? 'Menerapkan...' : 'Ya, Terapkan'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ThemaPage() {
  const tenantSlug = useAuthStore((s) => s.currentTenant?.slug)
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedGroup = searchParams.get('group')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedName, setSelectedName] = useState('')
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  // Primary: preset summaries from edge API
  const { data: presets = [], isLoading: presetsLoading } = useThemePresets()

  // Secondary: palette_group + mode from Supabase (not yet in edge API)
  const { data: groupMeta = [] } = useQuery({
    queryKey: ['theme-presets-grouping'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('theme_presets')
        .select('id, palette_group, mode')
        .eq('is_active', true)
      if (error) throw error
      return data as { id: string; palette_group: string | null; mode: string }[]
    },
    staleTime: 60_000,
  })

  // Merge grouping metadata into preset summaries
  const enriched = useMemo<EnrichedPreset[]>(() => {
    const lookup = new Map(groupMeta.map((g) => [g.id, g]))
    return presets.map((p) => ({
      ...p,
      palette_group: lookup.get(p.id)?.palette_group ?? null,
      mode: (lookup.get(p.id)?.mode ?? 'any') as 'dark' | 'light' | 'any',
    }))
  }, [presets, groupMeta])

  // Split: ungrouped flat + named groups map
  const { ungrouped, groups } = useMemo(() => {
    const ungrouped: EnrichedPreset[] = []
    const groupMap = new Map<string, EnrichedPreset[]>()

    for (const p of enriched) {
      if (!p.palette_group) {
        ungrouped.push(p)
      } else {
        if (!groupMap.has(p.palette_group)) groupMap.set(p.palette_group, [])
        groupMap.get(p.palette_group)!.push(p)
      }
    }

    return { ungrouped, groups: groupMap }
  }, [enriched])

  const applyTheme = useApplyTheme()

  function handleApplyClick(preset: ThemePresetSummary) {
    setSelectedId(preset.id)
    setSelectedName(preset.name)
    applyTheme.reset()
  }

  function handleConfirm() {
    if (!selectedId) return
    applyTheme.mutate(selectedId, {
      onSuccess: () => {
        setSelectedId(null)
        setSuccessMsg('Tema berhasil diterapkan! Perubahan akan terlihat di halaman publik segera.')
        setTimeout(() => setSuccessMsg(null), 6000)
      },
    })
  }

  // Current folder contents (null = root view)
  const currentGroupPresets = selectedGroup ? (groups.get(selectedGroup) ?? []) : null

  return (
    <FeatureGate feature="theme_preset">
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-bold">Tema Preset</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Terapkan paket tema siap pakai — warna, font, dan layout semua halaman sekaligus.
          </p>
        </div>
        <Link
          to="/settings/theme"
          className="shrink-0 text-sm text-muted-foreground hover:text-foreground hover:underline flex items-center gap-1"
        >
          <Paintbrush className="h-3.5 w-3.5" />
          Atur manual
        </Link>
      </div>

      {/* Success banner */}
      {successMsg && (
        <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <span className="flex-1">{successMsg}</span>
          <button onClick={() => setSuccessMsg(null)} className="text-green-600 hover:text-green-800">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Content */}
      {presetsLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
      ) : enriched.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Paintbrush className="h-10 w-10 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">Belum ada tema preset tersedia.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Platform admin akan menambahkan tema secara berkala.
          </p>
        </div>
      ) : currentGroupPresets !== null ? (
        /* ── Inside folder view ── */
        <FolderContentsView
          name={selectedGroup!}
          presets={currentGroupPresets}
          tenantSlug={tenantSlug}
          onBack={() => setSearchParams({})}
          onApply={handleApplyClick}
        />
      ) : (
        /* ── Root view: ungrouped flat + folder cards ── */
        <div className="space-y-6">
          {/* Ungrouped presets */}
          {ungrouped.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {ungrouped.map((preset) => (
                <PresetCard
                  key={preset.id}
                  preset={preset}
                  tenantSlug={tenantSlug}
                  onApply={() => handleApplyClick(preset)}
                />
              ))}
            </div>
          )}

          {/* Palette group folders */}
          {groups.size > 0 && (
            <div>
              {ungrouped.length > 0 && (
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Koleksi Palet
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...groups.entries()].map(([groupName, groupPresets]) => (
                  <FolderCard
                    key={groupName}
                    name={groupName}
                    presets={groupPresets}
                    onClick={() => setSearchParams({ group: groupName })}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirm dialog */}
      {selectedId && (
        <ConfirmDialog
          presetId={selectedId}
          presetName={selectedName}
          onConfirm={handleConfirm}
          onCancel={() => setSelectedId(null)}
          isPending={applyTheme.isPending}
          error={applyTheme.error?.message ?? null}
        />
      )}
    </div>
    </FeatureGate>
  )
}
